import { useEffect, useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { adAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { StellarDatePicker } from '../components/StellarFormControls';
import {
  Megaphone, Plus, DollarSign, Target, BarChart3, Trash2,
  Play, Pause, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const statusColors = {
  draft: 'bg-secondary text-secondary-foreground',
  active: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  paused: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  ended: 'bg-secondary text-muted-foreground',
};

const PLATFORM_COLORS = {
  facebook: '#1877F2', instagram: '#E1306C', linkedin: '#0A66C2',
  youtube: '#FF0000', pinterest: '#E60023', gmb: '#1A73E8', substack: '#FF6719'
};

export default function AdManagerPage() {
  const { currentWorkspace } = useWorkspace();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', platform: 'facebook', budget: '', daily_budget: '',
    objective: 'engagement', start_date: '', end_date: ''
  });

  useEffect(() => {
    if (currentWorkspace?.id) {
      setLoading(true);
      adAPI.list(currentWorkspace.id)
        .then(res => setCampaigns(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [currentWorkspace?.id]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Campaign name required'); return; }
    setCreating(true);
    try {
      await adAPI.create({
        ...form,
        workspace_id: currentWorkspace.id,
        budget: parseFloat(form.budget) || 0,
        daily_budget: parseFloat(form.daily_budget) || 0,
      });
      toast.success('Campaign created');
      setShowCreate(false);
      setForm({ name: '', platform: 'facebook', budget: '', daily_budget: '', objective: 'engagement', start_date: '', end_date: '' });
      const res = await adAPI.list(currentWorkspace.id);
      setCampaigns(res.data);
    } catch (err) {
      toast.error('Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusToggle = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      await adAPI.update(campaign.id, { status: newStatus });
      toast.success(`Campaign ${newStatus}`);
      const res = await adAPI.list(currentWorkspace.id);
      setCampaigns(res.data);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adAPI.delete(id);
      toast.success('Campaign deleted');
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
  const totalSpent = campaigns.reduce((sum, c) => sum + (c.spent || 0), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;

  return (
    <div className="p-6 space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Ad Manager</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage boost campaigns and ad spend across platforms.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="ad-manager-create-campaign-button">
          <Plus className="h-4 w-4 mr-2" /> New Campaign
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
                <Megaphone className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xl font-display font-semibold" data-font="display">{activeCampaigns}</p>
                <p className="text-xs text-muted-foreground">Active Campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-emerald-400" />
              </div>
              <div>
                <p className="text-xl font-display font-semibold" data-font="display">${totalBudget.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Total Budget</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl bg-card/60 backdrop-blur">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
                <Target className="h-4 w-4 text-orange-400" />
              </div>
              <div>
                <p className="text-xl font-display font-semibold" data-font="display">${totalSpent.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Total Spent</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-secondary rounded-xl animate-pulse" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20">
          <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-medium">No campaigns yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first ad campaign to boost content.</p>
          <Button className="mt-4" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" /> Create Campaign
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(campaign => (
            <Card key={campaign.id} className="bg-card/60 backdrop-blur">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-xs"
                      style={{ backgroundColor: PLATFORM_COLORS[campaign.platform] || 'hsl(var(--muted-foreground))' }}
                    >
                      {campaign.platform?.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{campaign.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="capitalize">{campaign.platform}</span>
                        <span>{campaign.objective}</span>
                        <span>${campaign.budget?.toFixed(2)} budget</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`text-xs ${statusColors[campaign.status] || 'bg-secondary'}`}>
                      {campaign.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleStatusToggle(campaign)}
                    >
                      {campaign.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => handleDelete(campaign.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Ad Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Campaign Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Summer Sale Boost" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Platform</Label>
                <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(PLATFORM_COLORS).map(p => (
                      <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Objective</Label>
                <Select value={form.objective} onValueChange={v => setForm(f => ({ ...f, objective: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engagement">Engagement</SelectItem>
                    <SelectItem value="traffic">Traffic</SelectItem>
                    <SelectItem value="conversions">Conversions</SelectItem>
                    <SelectItem value="awareness">Brand Awareness</SelectItem>
                    <SelectItem value="leads">Lead Generation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Budget ($)</Label>
                <Input type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} placeholder="500" />
              </div>
              <div className="space-y-2">
                <Label>Daily Budget ($)</Label>
                <Input type="number" value={form.daily_budget} onChange={e => setForm(f => ({ ...f, daily_budget: e.target.value }))} placeholder="25" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <StellarDatePicker value={form.start_date} onChange={(d) => setForm(f => ({ ...f, start_date: d }))} placeholder="Start" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <StellarDatePicker value={form.end_date} onChange={(d) => setForm(f => ({ ...f, end_date: d }))} placeholder="End" minDate={form.start_date || undefined} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
